import React, { useEffect, useState } from 'react';
import { Download, X, Video } from 'lucide-react';

const DISMISS_KEY = 'kaplie_pwa_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // re-prompt after 7 days

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // small delay so the prompt feels intentional, not jarring
      setTimeout(() => setVisible(true), 1500);
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'dismissed') {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      }
    } catch (e) {
      // user cancelled — just dismiss
    } finally {
      setDeferredPrompt(null);
      setVisible(false);
      setInstalling(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-md animate-fade-in-up"
    >
      <div className="relative backdrop-blur-2xl bg-black/80 border border-[#D4AF37]/25 rounded-2xl p-4 shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_40px_rgba(212,175,55,0.08)] flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(212,175,55,0.25)]">
          <Video className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-0.5">Install Kaplie</p>
          <p className="text-sm font-semibold text-white leading-tight">Add to your home screen</p>
          <p className="text-xs text-zinc-400 mt-1 leading-snug">Faster launches, no browser bar, and a permanent gold K on your screen.</p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleInstall}
              disabled={installing}
              data-testid="install-prompt-accept"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black text-xs font-semibold rounded-full hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50"
            >
              {installing ? (
                <div className="w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              Install
            </button>
            <button
              onClick={handleDismiss}
              data-testid="install-prompt-dismiss"
              className="px-3 py-2 text-xs text-zinc-400 hover:text-white transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute top-3 right-3 p-1 rounded-md hover:bg-white/[0.06] transition-colors"
        >
          <X className="w-3.5 h-3.5 text-zinc-500" />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
